import { useState, useEffect, useMemo } from 'react'
import { formatDate, parseDate } from '../../../utils/dateUtils'
import usePermission from '../../../hooks/usePermission'
import {
    fetchDGRReports,
    createDGRReport,
    updateDGRReport,
    deleteDGRReport,
    fetchPlantListForDGR,
} from '../services/dgrService'

export const useDGR = () => {
    // State management
    const [reports, setReports] = useState([])
    const [filteredReports, setFilteredReports] = useState([])
    const [loading, setLoading] = useState(true)
    const [plantLoading, setPlantLoading] = useState(false)
    const [selectedReport, setSelectedReport] = useState(null)
    const [showForm, setShowForm] = useState(false)
    const [showDetail, setShowDetail] = useState(false)
    const [reportToDelete, setReportToDelete] = useState(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedPlant, setSelectedPlant] = useState(null)
    const [plants, setPlants] = useState([])
    const [selectedDate, setSelectedDate] = useState(parseDate())
    const [importModalOpen, setImportModalOpen] = useState(false)
    const [importStatus, setImportStatus] = useState({
        open: false,
        message: '',
        severity: 'success',
        errors: [],
    })

    const [filterMode, setFilterMode] = useState('date') // toggle date or plant
    const [dateSortDirection, setDateSortDirection] = useState('desc')
    const [startDate, setStartDate] = useState(parseDate().subtract(7, 'days'))
    const [endDate, setEndDate] = useState(parseDate())

    // Load reports on component mount
    useEffect(() => {
        loadReports()
        loadPlants()
    }, [])

    // Filter reports when filters change
    useEffect(() => {
        filterReports()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reports, searchTerm, selectedPlant, selectedDate, filterMode, startDate, endDate])

    const loadPlants = async () => {
        try {
            setPlantLoading(true)
            const plantList = await fetchPlantListForDGR()
            setPlants(plantList)
        } catch (error) {
            console.error('Error loading plants:', error)
            setPlants([])
        } finally {
            setPlantLoading(false)
        }
    }

    const loadReports = async () => {
        try {
            setLoading(true)
            // Load all reports since we're filtering by date/plant on frontend
            const data = await fetchDGRReports({})
            console.log('Reports loaded:', data.length, 'reports')
            setReports(data)
        } catch (error) {
            console.error('Error loading reports:', error)
            setReports([])
        } finally {
            setLoading(false)
        }
    }

    const filterReports = () => {
        let filtered = [...reports]

        if (filterMode === 'date') {
            // Date mode: filter by exact date match
            if (selectedDate) {
                filtered = filtered.filter((report) => {
                    try {
                        const reportDate = parseDate(report.date || report.reportDate)
                        const filterDate = parseDate(selectedDate)

                        if (!reportDate.isValid() || !filterDate.isValid()) {
                            return false
                        }

                        return reportDate.isSame(filterDate, 'day')
                    } catch (error) {
                        console.error('Date filtering error:', error)
                        return false
                    }
                })
            }

            // Additional plant filter in date mode
            if (selectedPlant) {
                const searchPlantId = (selectedPlant.id || selectedPlant._id || selectedPlant).toString()
                filtered = filtered.filter((report) => {
                    const reportPlantId = (report.plantId?.id || report.plantId?._id || report.plantId).toString()
                    return reportPlantId === searchPlantId
                })
            }
        } else if (filterMode === 'plant') {
            // Plant mode: filter by plant and date range
            if (selectedPlant) {
                const searchPlantId = (selectedPlant.id || selectedPlant._id || selectedPlant).toString()
                filtered = filtered.filter((report) => {
                    const reportPlantId = (report.plantId?.id || report.plantId?._id || report.plantId).toString()
                    return reportPlantId === searchPlantId
                })
            }

            // Date range filter
            if (startDate && endDate) {
                filtered = filtered.filter((report) => {
                    try {
                        const reportDate = parseDate(report.date || report.reportDate)
                        const start = parseDate(startDate)
                        const end = parseDate(endDate)

                        if (!reportDate.isValid()) {
                            return false
                        }

                        return (reportDate.isSame(start, 'day') || reportDate.isAfter(start, 'day')) &&
                            (reportDate.isSame(end, 'day') || reportDate.isBefore(end, 'day'))
                    } catch (error) {
                        console.error('Date range filtering error:', error)
                        return false
                    }
                })
            }
        }

        setFilteredReports(filtered)
    }

    const toggleDateSort = () => {
        setDateSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    }

    const sortedReports = useMemo(() => {
        const arr = [...filteredReports]
        const compareDates = (a, b) => {
            try {
                const da = parseDate(a.date || a.reportDate)
                const db = parseDate(b.date || b.reportDate)
                const va = da.isValid() ? da.valueOf() : null
                const vb = db.isValid() ? db.valueOf() : null
                // Place invalid dates at the end
                if (va === null && vb === null) return 0
                if (va === null) return 1
                if (vb === null) return -1
                return dateSortDirection === 'asc' ? va - vb : vb - va
            } catch (err) {
                // fallback: keep original order on error
                return 0
            }
        }
        arr.sort(compareDates)
        return arr
    }, [filteredReports, dateSortDirection])

    const handleCreateReport = () => {
        setSelectedReport(null)
        setEditMode(false)
        setShowForm(true)
    }

    const handleEditReport = (report) => {
        setSelectedReport({
            ...report,
            id: report.id?.$oid || report.id || null,
        })
        setEditMode(true)
        setShowForm(true)
    }

    const handleViewReport = (report) => {
        setSelectedReport({
            ...report,
            id: report.id?.$oid || report.id || null,
        })
        setShowDetail(true)
    }

    const handleDeleteReport = async (report) => {
        const reportId = report.id?.$oid || report.id
        if (!reportId) {
            console.error('No valid ID found for report:', report)
            return
        }
        setReportToDelete({ ...report, id: reportId })
        setShowDeleteModal(true)
    }

    const confirmDeleteReport = async () => {
        if (!reportToDelete) return

        try {
            await deleteDGRReport(reportToDelete.id)
            // Optimistic update: Remove from local state instead of reloading
            setReports((prev) => prev.filter((r) => r.id !== reportToDelete.id))
        } catch (error) {
            console.error('Error deleting report', error)
            // Reload on error to sync with server
            await loadReports()
        } finally {
            setShowDeleteModal(false)
            setReportToDelete(null)
        }
    }

    const handleCancelDelete = () => {
        setShowDeleteModal(false)
        setReportToDelete(null)
    }

    const handleFormSubmit = async (reportData) => {
        try {
            if (editMode && selectedReport) {
                const updatedReport = await updateDGRReport(selectedReport.id, reportData)
                // Optimistic update
                setReports((prev) =>
                    prev.map((r) =>
                        r.id === selectedReport.id
                            ? { ...updatedReport, id: updatedReport.id?.$oid || updatedReport.id }
                            : r
                    )
                )
            } else {
                const newReport = await createDGRReport(reportData)
                // Optimistic update
                setReports((prev) => [
                    { ...newReport, id: newReport.id?.$oid || newReport.id },
                    ...prev,
                ])
            }
            setShowForm(false)
            setSelectedReport(null)
        } catch (error) {
            console.error('Error saving report:', error)
            await loadReports()
        }
    }

    const handleFormClose = () => {
        setShowForm(false)
        setSelectedReport(null)
        setEditMode(false)
    }

    const handleDetailClose = () => {
        setShowDetail(false)
        setSelectedReport(null)
    }

    const handleOpenImportModal = () => {
        setImportModalOpen(true)
    }

    const handleCloseImportModal = () => {
        setImportModalOpen(false)
    }

    const handleImportSuccess = async (importResult) => {
        try {
            const created = importResult?.created || 0
            const skipped = importResult?.skipped || 0
            const errors = importResult?.errors || []
            const total = created + skipped

            const message = `Imported ${created} out of ${total} reports.`
            const severity = errors && errors.length > 0 ? 'error' : skipped > 0 ? 'warning' : 'success'

            setImportStatus({ open: true, message, severity, errors })
        } catch (err) {
            console.error('Error preparing import notification:', err)
        }

        await loadReports()
    }

    const { is, can } = usePermission()

    return {
        state: {
            reports, filteredReports, loading, plantLoading, selectedReport, showForm, showDetail,
            reportToDelete, showDeleteModal, editMode, searchTerm, selectedPlant, plants,
            selectedDate, importModalOpen, importStatus, filterMode, dateSortDirection,
            startDate, endDate, sortedReports
        },
        setters: {
            setSearchTerm, setSelectedPlant, setSelectedDate, setFilterMode, setStartDate, setEndDate,
            setImportStatus, setDateSortDirection, setImportModalOpen, setShowDetail
        },
        actions: {
            loadPlants, loadReports, filterReports, toggleDateSort,
            handleCreateReport, handleEditReport, handleViewReport, handleDeleteReport,
            confirmDeleteReport, handleCancelDelete, handleFormSubmit, handleFormClose,
            handleDetailClose, handleOpenImportModal, handleCloseImportModal, handleImportSuccess
        },
        permissions: {
            can, is
        }
    }
}
